import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
    IMultiYearAvailableLanguage,
    IMultiYearComparisonResponseDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import * as fromActions from '../../../actions/analytics.actions';
import { AnalyticsCompanyEntry, selectAnalyticsForCompany } from '../../../reducer/analytics.reducer';
import {
    MultiYearExportModalComponent,
    MultiYearExportModalData,
    MultiYearExportModalResult,
} from '../export-modal/multi-year-export-modal.component';
import {
    MultiYearLanguageSelectionModalComponent,
    MultiYearLanguageSelectionModalData,
    MultiYearLanguageSelectionModalResult,
} from '../language-selection-modal/multi-year-language-selection-modal.component';
import { ControlsState, DeltaMode, ReportScope } from '../models/controls.model';
import { collectExpandableNodeIds } from '../models/tree-walk.util';

interface ContainerVM {
    loading: boolean;
    hasError: boolean;
    state: 'empty' | 'normal' | 'loading' | 'error';
    harmonized: IMultiYearComparisonResponseDTO | null;
    controls: ControlsState | null;
    currencies: string[];
    /** True iff every expandable node in the harmonized tree is expanded. */
    isAllExpanded: boolean;
    /** True between PDF-export dispatch and the WebSocket result. */
    exportInFlight: boolean;
}

/**
 * Orchestrator for the Mehrjahresvergleich. Handles loading, error and the
 * empty/normal state machine. Delegates the heavy rendering to <normal-state-view>.
 */
@Component({
    selector: 'multi-year-comparison-container',
    templateUrl: './multi-year-comparison-container.component.html',
    styleUrls: ['./multi-year-comparison-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiYearComparisonContainerComponent implements OnChanges, OnDestroy {
    @Input() public companyId!: string;

    public vm$!: Observable<ContainerVM>;

    private readonly destroy$ = new Subject<void>();
    private languageDialogRef: MatDialogRef<
        MultiYearLanguageSelectionModalComponent,
        MultiYearLanguageSelectionModalResult
    > | null = null;
    private exportDialogRef: MatDialogRef<
        MultiYearExportModalComponent,
        MultiYearExportModalResult
    > | null = null;

    constructor(
        private readonly store: Store,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['companyId'] && this.companyId) {
            // Flush the OUTGOING company's dirty MYC prefs before switching.
            // Without this a user toggling settings on Company A and
            // navigating straight to Company B would lose the unsaved
            // changes (the 30s periodic flush might not have ticked yet).
            const previous = changes['companyId'].previousValue;
            if (typeof previous === 'string' && previous.length > 0 && previous !== this.companyId) {
                this.store.dispatch(fromActions.flushMycPreferences({ companyId: previous }));
            }

            // Close any open dialog before swapping companies — the dialog
            // captured the OLD `companyId` in its action dispatches; letting
            // it confirm later would persist the old company's selection on
            // the new company.
            this.closeOpenDialogs();

            // Reset the destroy-stream so any prior language-modal subscription
            // gets cleaned up when the user navigates between companies.
            this.destroy$.next();

            this.store.dispatch(fromActions.loadAnalyticsForCompany({ companyId: this.companyId }));
            this.vm$ = this.store
                .select(selectAnalyticsForCompany(this.companyId))
                .pipe(map((entry) => this.toViewModel(entry)));

            // Watch for BE-signalled language mismatch and open the modal as
            // soon as `pendingLanguageSelection` flips from null → list.
            // Drives the entire 409-recovery flow without any imperative
            // wiring from the effect.
            this.store
                .select(selectAnalyticsForCompany(this.companyId))
                .pipe(
                    map((entry) => entry?.pendingLanguageSelection ?? null),
                    distinctUntilChanged(),
                    filter((pending): pending is IMultiYearAvailableLanguage[] => pending != null && pending.length > 0),
                    takeUntil(this.destroy$),
                )
                .subscribe((availableLanguages) => this.openLanguageSelectionModal(availableLanguages));
        }
    }

    public ngOnDestroy(): void {
        // Flush any pending MYC-preferences changes for the current company
        // before the component goes away. In-app navigation (router) tears
        // the container down → this catches the "user clicked then left
        // the page" case before the 30s periodic flush would have run.
        // The flush effect itself is a no-op when nothing's dirty.
        if (this.companyId) {
            this.store.dispatch(fromActions.flushMycPreferences({ companyId: this.companyId }));
        }
        this.closeOpenDialogs();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Close any dialog the container has opened. Called from `ngOnDestroy`
     * and from the `companyId` switch in `ngOnChanges` so an in-flight
     * dialog can't outlive the company context it was opened for.
     *
     * MatDialogRef.close() is idempotent — safe to call even when the
     * dialog is already closed.
     */
    private closeOpenDialogs(): void {
        this.languageDialogRef?.close();
        this.languageDialogRef = null;
        this.exportDialogRef?.close();
        this.exportDialogRef = null;
    }

    /**
     * Open the language-selection modal in response to a fresh
     * `pendingLanguageSelection` from the store.
     *
     * `disableClose: true` makes this a **forced choice** — backdrop click
     * and ESC are blocked, and the modal template has no Cancel button.
     * The user must pick a language for the modal to close, which means
     * the analytics entry can never end up in the "no harmonized, no
     * error, no pending" zombie state that would otherwise show an
     * infinite loading spinner.
     */
    private openLanguageSelectionModal(availableLanguages: IMultiYearAvailableLanguage[]): void {
        // Guard against the rare double-open if `pendingLanguageSelection`
        // gets re-emitted while a previous modal is still on screen.
        if (this.languageDialogRef != null) return;

        this.languageDialogRef = this.dialog.open<
            MultiYearLanguageSelectionModalComponent,
            MultiYearLanguageSelectionModalData,
            MultiYearLanguageSelectionModalResult
        >(MultiYearLanguageSelectionModalComponent, {
            data: { availableLanguages },
            panelClass: 'myc-lang-modal-panel',
            autoFocus: false,
            disableClose: true,
        });
        this.languageDialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            this.languageDialogRef = null;
            // With `disableClose: true` + no Cancel button in the template,
            // the only way to close is a confirmed pick → `result` is the
            // chosen locale string. The defensive null-check is here in
            // case of programmatic close (e.g. `ngOnDestroy` below).
            if (typeof result === 'string' && result.length > 0) {
                this.store.dispatch(
                    fromActions.setMultiYearLanguage({ companyId: this.companyId, language: result }),
                );
            }
        });
    }

    /**
     * Permanent language switch from the controls-bar dropdown. Routes
     * through the same action the modal uses → effect re-fetches.
     */
    public onLanguageChanged(language: string): void {
        this.store.dispatch(fromActions.setMultiYearLanguage({ companyId: this.companyId, language }));
    }

    public onYearToggled(yearLabel: string): void {
        this.store.dispatch(fromActions.toggleYear({ companyId: this.companyId, yearLabel }));
    }

    public onDeltaModeChanged(mode: DeltaMode): void {
        this.store.dispatch(fromActions.setDeltaMode({ companyId: this.companyId, mode }));
    }

    public onReportScopeChanged(scope: ReportScope): void {
        this.store.dispatch(fromActions.setReportScope({ companyId: this.companyId, scope }));
    }

    public onStructModeToggled(): void {
        this.store.dispatch(fromActions.toggleStructMode({ companyId: this.companyId }));
    }

    public onNodeToggled(nodeId: string): void {
        this.store.dispatch(fromActions.toggleNode({ companyId: this.companyId, nodeId }));
    }

    public onShowAllDetails(): void {
        this.store.dispatch(fromActions.expandAllNodes({ companyId: this.companyId }));
    }

    public onHideAllDetails(): void {
        this.store.dispatch(fromActions.collapseAllNodes({ companyId: this.companyId }));
    }

    public onCreateProject(): void {
        // `..` is only safe in `router.navigate` when paired with
        // `relativeTo` — without it Angular treats the command array as
        // absolute and the `..` doesn't resolve to anything sensible.
        // Pin to the container's `ActivatedRoute` so it walks up from
        // wherever the route tree actually placed this component.
        this.router.navigate(['..', 'project'], {
            queryParams: { createProject: 1 },
            relativeTo: this.route,
        });
    }

    /**
     * Open the export modal. The modal lets the user pick which Groups / FLIs /
     * Accounts appear in the PDF (default: all). When the user confirms, we
     * dispatch the export action with the chosen ids; the effect then runs
     * the V2 publish-then-WebSocket roundtrip exactly like before.
     */
    public onExportPdf(): void {
        // Read the harmonized DTO + persisted export prefs out of the store
        // snapshot. The modal renders its initial state from the prefs so
        // every reopen pre-selects what the user confirmed last time.
        this.store
            .select(selectAnalyticsForCompany(this.companyId))
            .pipe(take(1))
            .subscribe((entry) => {
                if (!entry?.harmonized || !entry.controls) return;
                // Reuse the field instead of a local — the container's
                // lifecycle hooks (`ngOnChanges` company-switch, `ngOnDestroy`)
                // close any open dialog so an in-flight modal can't outlive
                // the company context it was opened for. Without the field
                // they'd have no handle to close it programmatically.
                this.exportDialogRef = this.dialog.open<
                    MultiYearExportModalComponent,
                    MultiYearExportModalData,
                    MultiYearExportModalResult
                >(MultiYearExportModalComponent, {
                    // selectedYears mirrors the page's current pill-selection so
                    // the modal hides branches that are zero across those years —
                    // identical filter to the on-screen view.
                    // `initialPreferences` pre-populates the Section-Toggles
                    // / Show-Accounts / Show-Ratios / tree-checkbox state from
                    // the last confirmed export (loaded from
                    // `Company.mycPreferences`). Stale node ids are already
                    // filtered out by the reducer at `loadAnalyticsSuccess`
                    // against the live tree.
                    data: {
                        harmonized: entry.harmonized,
                        selectedYears: entry.controls.selectedYears,
                        initialPreferences: entry.exportPreferences ?? undefined,
                    },
                    panelClass: 'myc-export-modal-panel',
                    autoFocus: false,
                });
                this.exportDialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
                    this.exportDialogRef = null;
                    // Cancel → no persist, no export. UX decision: Cancel
                    // does NOT save anything (only Confirm persists).
                    if (!result || result.selectedNodeIds.length === 0) return;

                    // Persist the final modal selection to `Company.mycPreferences`.
                    // Fires off in parallel with the export request — both
                    // are independent side effects.
                    this.store.dispatch(fromActions.persistExportModalConfirm({
                        companyId: this.companyId,
                        includeBalance: result.includeBalance,
                        includeIncome: result.includeIncome,
                        showAccounts: result.showAccounts,
                        showRatios: result.showRatios,
                        selectedNodeIds: result.selectedNodeIds,
                    }));

                    this.store.dispatch(fromActions.requestMultiYearPdf({
                        companyId: this.companyId,
                        selectedNodeIds: result.selectedNodeIds,
                        showAccounts: result.showAccounts,
                        includeBalance: result.includeBalance,
                        includeIncome: result.includeIncome,
                        showRatios: result.showRatios,
                    }));
                });
            });
    }

    private toViewModel(entry: AnalyticsCompanyEntry | null): ContainerVM {
        const fallback: ContainerVM = {
            loading: true,
            hasError: false,
            state: 'loading',
            harmonized: null,
            controls: null,
            currencies: [],
            isAllExpanded: false,
            exportInFlight: false,
        };
        if (entry == null) return fallback;
        if (entry.error) {
            return { ...fallback, loading: false, hasError: true, state: 'error' };
        }
        if (entry.loading || !entry.harmonized) {
            return { ...fallback, loading: true, state: 'loading' };
        }
        const harmonized = entry.harmonized;
        const isEmpty = harmonized.yearLabels.length === 0;
        return {
            loading: false,
            hasError: false,
            state: isEmpty ? 'empty' : 'normal',
            harmonized,
            controls: entry.controls,
            currencies: harmonized.currencies,
            isAllExpanded: this.computeIsAllExpanded(harmonized, entry.controls.expandedNodes),
            exportInFlight: entry.exportInFlight,
        };
    }

    /** True iff every expandable node in the harmonized tree is in `expandedNodes`. */
    private computeIsAllExpanded(
        harmonized: IMultiYearComparisonResponseDTO,
        expandedNodes: string[],
    ): boolean {
        const expandableIds = collectExpandableNodeIds(harmonized);
        if (expandableIds.length === 0) return false;
        const set = new Set(expandedNodes);
        return expandableIds.every((id) => set.has(id));
    }
}
